import React from 'react';

import { IconButton, InputAdornment, Typography, MenuItem, Box } from '@mui/material';
import TextField from '@mui/material/TextField';
import { withStyles } from '@mui/styles';
import clsx from 'clsx';
import Tooltip from 'components/Tooltip';
import PropTypes from 'prop-types';

import { styles } from './style';

import InputHelperText from './InputHelperText';

const Input = ({
    label,
    variant,
    value,
    select,
    dataList,
    error,
    name,
    type,
    handleChange,
    placeholder,
    disabled,
    helperText,
    labelClass,
    icon,
    classes,
    className,
    startAdornment,
    StartAdornmentIcon,
    endAdornment,
    EndAdornmentIcon,
    emdAdormentIconClass,
    endAdormentClick,
    iconCompoent,
    isRequired,
    showCharLimit = false,
    maxCharLimit,
    charLimitClass,
    isErrIconResponsive,
    hideErrIconOnDesktop = true,
    tooltipContent,
    overrideStyles,
    autoFocus = false,
    ...restprops
}) => {
    const charCount = value?.length || 0;
    const hasCharLimitExceeded = charCount > maxCharLimit;
    return (
        <Box className={classes.inputContainer}>
            {label && (
                <Typography className={clsx(classes.labelBaseClass, labelClass)}>
                    {label}
                    {isRequired && <span className={classes.asterisk}>*</span>}
                    {tooltipContent && (
                        <Tooltip
                            title={tooltipContent}
                            ariaLabel="valid-id"
                            className={classes.inputToolTip}
                            placement="right"
                            arrow
                        />
                    )}
                </Typography>
            )}

            {select ? (
                <TextField
                    {...restprops}
                    fullWidth
                    select
                    value={value}
                    name={name}
                    type={type}
                    error={error}
                    placeholder={placeholder}
                    helperText={helperText}
                    variant={variant}
                    disabled={disabled}
                    onChange={handleChange}
                    className={classes.textField}
                    margin="normal"
                    slotProps={{
                        formHelperText: {
                            component: () => (
                                <InputHelperText
                                    maxCharLimit={maxCharLimit}
                                    isErrIconResponsive={isErrIconResponsive}
                                    calcCharCount={charCount}
                                    helperText={helperText}
                                    showCharLimit={showCharLimit}
                                    hideErrIconOnDesktop={hideErrIconOnDesktop}
                                />
                            ),
                        },
                        inputLabel: {
                            classes: {
                                root: classes.cssLabel,
                                focused: classes.cssFocused,
                            },
                        },
                        select: {
                            IconComponent: iconCompoent,
                            classes: {
                                select: classes.select,
                                iconOutlined: classes.selectIconOutlined,
                            },
                        },
                        input: {
                            classes: {
                                root: clsx(
                                    variant === 'outlined'
                                        ? classes.cssOutlinedInput
                                        : classes.cssFilledInput,
                                    className
                                ),
                                focused: classes.cssFocused,
                                notchedOutline: classes.notchedOutline,
                                error: classes.error,
                                disabled: classes.disabled,
                                input: classes.inputPlaceholder,
                            },
                            ...restprops.InputProps,
                        },
                    }}
                >
                    {dataList.map((data) => {
                        return (
                            <MenuItem
                                key={data.value}
                                value={data.value}
                                className={classes.menuItem}
                            >
                                {data.label}
                            </MenuItem>
                        );
                    })}
                </TextField>
            ) : (
                <TextField
                    {...restprops}
                    fullWidth
                    value={value}
                    name={name}
                    type={type}
                    error={error || hasCharLimitExceeded}
                    placeholder={placeholder}
                    helperText={helperText}
                    variant={variant}
                    disabled={disabled}
                    onChange={handleChange}
                    className={classes.textField}
                    margin="normal"
                    autoFocus={autoFocus}
                    slotProps={{
                        formHelperText: {
                            component: () => (
                                <InputHelperText
                                    maxCharLimit={maxCharLimit}
                                    isErrIconResponsive={isErrIconResponsive}
                                    calcCharCount={charCount}
                                    helperText={helperText}
                                    showCharLimit={showCharLimit}
                                    hideErrIconOnDesktop={hideErrIconOnDesktop}
                                />
                            ),
                        },
                        inputLabel: {
                            classes: {
                                root: classes.cssLabel,
                                focused: classes.cssFocused,
                            },
                        },
                        input: {
                            maxLength: maxCharLimit + 1,
                            readOnly: restprops.readOnly,
                            classes: {
                                root: clsx(
                                    variant === 'outlined'
                                        ? classes.cssOutlinedInput
                                        : classes.cssFilledInput,
                                    className
                                ),
                                focused: classes.cssFocused,
                                notchedOutline:
                                    variant !== 'filled'
                                        ? classes.notchedOutline
                                        : classes.notchedFilled,
                                error: classes.error,
                                disabled: classes.disabled,
                                input: classes.inputPlaceholder,
                            },
                            startAdornment: startAdornment && (
                                <InputAdornment
                                    className={
                                        overrideStyles?.startAdornment
                                            ? overrideStyles?.startAdornment
                                            : classes.startAdornment
                                    }
                                    position="start"
                                >
                                    <StartAdornmentIcon />
                                </InputAdornment>
                            ),
                            endAdornment: endAdornment && (
                                <InputAdornment position="end">
                                    <IconButton
                                        className={clsx(classes.endIcon, emdAdormentIconClass)}
                                        onClick={endAdormentClick}
                                        disabled={disabled}
                                    >
                                        <EndAdornmentIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            ...restprops.InputProps,
                        },
                    }}
                />
            )}
            {showCharLimit && !error && (
                <Typography className={charLimitClass}>
                    {charCount}/{maxCharLimit}
                </Typography>
            )}
        </Box>
    );
};

Input.propTypes = {
    label: PropTypes.string,
    variant: PropTypes.string,
    value: PropTypes.string,
    select: PropTypes.bool,
    dataList: PropTypes.array,
    error: PropTypes.bool,
    name: PropTypes.string,
    type: PropTypes.string,
    handleChange: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    helperText: PropTypes.string,
    labelClass: PropTypes.string,
    icon: PropTypes.node,
    classes: PropTypes.object,
    className: PropTypes.string,
    startAdornment: PropTypes.bool,
    StartAdornmentIcon: PropTypes.oneOf(['node', 'element']),
    endAdornment: PropTypes.bool,
    EndAdornmentIcon: PropTypes.node,
    emdAdormentIconClass: PropTypes.string,
    endAdormentClick: PropTypes.func,
    iconCompoent: PropTypes.node,
    isRequired: PropTypes.bool,
    showCharLimit: PropTypes.bool,
    maxCharLimit: PropTypes.number,
    charLimitClass: PropTypes.string,
    isErrIconResponsive: PropTypes.bool,
    hideErrIconOnDesktop: PropTypes.bool,
    tooltipContent: PropTypes.any,
    overrideStyles: PropTypes.object,
    autoFocus: PropTypes.bool,
};

Input.defaultProps = {
    disabled: false,
    isRequired: false,
    showCharLimit: false,
    isErrIconResponsive: false,
    autoFocus: false,
};

export default withStyles(styles)(Input);
